/**
 * Marketing-site configuration.
 *
 * In **production**, `docker-entrypoint.sh` writes `/app/dist/config.js` at
 * container start from the app's env vars; `index.html` loads it before the
 * bundle, populating `window.__GOALPOST_CONFIG__`. The bundle contains no
 * environment-specific values, so the same image runs in stage and prod.
 *
 * In **local development** (`npm run dev`), Vite serves the placeholder
 * `public/config.js` (an empty object), so each getter falls back to its
 * `VITE_*` env var.
 */

interface GoalpostRuntimeConfig {
  apiUrl?: string;
  webappUrl?: string;
  sentryDsn?: string;
  sentryEnvironment?: string;
  // Experiment keys currently enrolling. The bundle/env registry's on/off
  // switch — see getEnabledExperiments() and docs/experimentation.md.
  experiments?: string[];
}

declare global {
  interface Window {
    __GOALPOST_CONFIG__?: GoalpostRuntimeConfig;
  }
}

function runtimeConfig(): GoalpostRuntimeConfig {
  if (typeof window === 'undefined') return {};
  return window.__GOALPOST_CONFIG__ ?? {};
}

// Origin of the API (e.g. https://api.movegoalpost.com). The AI-integration
// docs page renders the MCP endpoint URL from it, the ad-conversion beacon
// and the /pricing page's live Pro prices POST to it directly (CORS admits
// the WWW_URL origin — see api/src/server.ts), and the Sentry tunnel routes
// through it too.
export function getApiBaseUrl(): string {
  const fromRuntime = runtimeConfig().apiUrl;
  if (fromRuntime) return fromRuntime;
  return import.meta.env.VITE_API_URL || '';
}

// Origin of the product webapp (e.g. https://app.movegoalpost.com), where
// signup/login and the authenticated product live. Named WEBAPP_URL to match
// the API's existing canonical name for this origin (rather than introduce a
// second name); its value is the `app.` host in prod. Marketing CTAs are
// cross-origin links to this host; see webappUrl().
function getWebappBaseUrl(): string {
  const fromRuntime = runtimeConfig().webappUrl;
  if (fromRuntime) return fromRuntime;
  return import.meta.env.VITE_WEBAPP_URL || '';
}

// Build an absolute URL into the product webapp for a given path. With no
// webapp origin configured (local dev, or a single-origin deploy), falls back
// to a same-origin relative path so links still resolve.
export function webappUrl(path: string): string {
  const base = getWebappBaseUrl();
  if (!base) return path;
  return `${base.replace(/\/$/, '')}${path}`;
}

export function getSentryDsn(): string | undefined {
  const fromRuntime = runtimeConfig().sentryDsn;
  if (fromRuntime) return fromRuntime;
  return import.meta.env.VITE_SENTRY_DSN;
}

export function getSentryEnvironment(): string | undefined {
  const fromRuntime = runtimeConfig().sentryEnvironment;
  if (fromRuntime) return fromRuntime;
  return import.meta.env.VITE_SENTRY_ENVIRONMENT;
}

// Git revision baked into the bundle at build time (VITE_GIT_REVISION from
// the docker build-arg), used as Sentry's release identifier once telemetry
// is wired up.
export function getGitRevision(): string | undefined {
  return import.meta.env.VITE_GIT_REVISION;
}

// The experiment keys currently enrolling — the homepage experiments' on/off
// switch, delivered through the runtime-config registry (see
// docs/experimentation.md). In prod these come from
// `window.__GOALPOST_CONFIG__.experiments`, written by docker-entrypoint.sh from
// the `EXPERIMENTS` env var at container start; in local dev they fall back to a
// comma-separated `VITE_EXPERIMENTS`. An experiment runs only when its key
// appears here, so enrollment is known synchronously at load with no API call.
export function getEnabledExperiments(): string[] {
  const fromRuntime = runtimeConfig().experiments;
  if (Array.isArray(fromRuntime)) {
    return fromRuntime.filter((key): key is string => typeof key === 'string' && key.length > 0);
  }
  const fromEnv = import.meta.env.VITE_EXPERIMENTS;
  if (typeof fromEnv === 'string' && fromEnv.trim()) {
    return fromEnv.split(',').map((key) => key.trim()).filter(Boolean);
  }
  return [];
}
